import React, { useCallback, useState } from 'react';
import {
  Table,
  Button,
  Select,
  DatePicker,
  Tag,
  Row,
  Col,
  Space,
  Tooltip,
  Input,
  message,
  Modal,
  Typography,
  Result,
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import Card from 'components/card';
import SITELINKS from 'constants/sitelinks';
import { useMarketplaces } from 'services/MarketplaceService';
import { useStoresByMarketplace } from 'services/StoreService';
import { useDrivers } from 'services/DriverService';
import { Paquer } from 'interfaces/paquer';
import {
  Collection,
  CollectionStatus,
  CollectionType,
  COLLECTION_STATUS_COLORS,
  COLLECTION_STATUS_LABELS,
  COLLECTION_TYPE_COLORS,
  COLLECTION_TYPE_LABELS,
  collectionType,
  collectionWindow,
  declaredText,
  HISTORY_STATUSES,
  TRANSIT_STATUSES,
  collectionsResultStatus,
  useCollections,
  useCollectionPoints,
  useCancelCollection,
} from 'services/CollectionsV2Service';
import { RouteSearch } from '../journeys/lists/kind';
import CollectionDetailModal from './CollectionDetailModal';
import CollectionV2Intake from '../collectionIngreso/CollectionV2Intake';

const PAGE_SIZE = 20;

type Mode = 'transit' | 'history';

const MODE = {
  transit: { title: 'Colectas en tránsito', statuses: TRANSIT_STATUSES },
  history: { title: 'Histórico de colectas', statuses: HISTORY_STATUSES },
};

const CANCELABLE: CollectionStatus[] = ['requested', 'routed', 'arrived'];

const INGRESABLE: CollectionStatus[] = ['collected', 'ingesting'];

const TYPE_OPTIONS = (
  Object.keys(COLLECTION_TYPE_LABELS) as CollectionType[]
).map((type) => ({ label: COLLECTION_TYPE_LABELS[type], value: type }));

const Collections: React.FC<{ mode: Mode }> = ({ mode }: { mode: Mode }) => {
  const { title, statuses: modeStatuses } = MODE[mode];
  const statusOptions = modeStatuses.map((status) => ({
    label: COLLECTION_STATUS_LABELS[status],
    value: status,
  }));
  const params = new URLSearchParams(useLocation().search);
  const fromUrl = (name: string) => Number(params.get(name)) || null;
  const [marketplaceId, setMarketplaceId] = useState<number | null>(
    fromUrl('marketplaceId'),
  );
  const [storeId, setStoreId] = useState<number | null>(fromUrl('storeId'));
  const [driverId, setDriverId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<CollectionStatus[]>([]);
  const [types, setTypes] = useState<CollectionType[]>([]);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [page, setPage] = useState(0);
  const [pointId, setPointId] = useState<number | null>(null);
  const [q, setQ] = useState<string | undefined>();
  const onSearch = useCallback((value?: string) => {
    setPage(0);
    setQ(value);
  }, []);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [intakeId, setIntakeId] = useState<number | null>(null);
  const [toCancel, setToCancel] = useState<Collection | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const { data: marketplaces = [], isLoading: marketplacesLoading } =
    useMarketplaces();
  const { data: stores = [], isLoading: storesLoading } =
    useStoresByMarketplace(marketplaceId ?? 0);
  const { data: driversData = [], isLoading: driversLoading } = useDrivers();
  const drivers = driversData as Paquer[];
  const { data: pointsData } = useCollectionPoints({ pageSize: 100 });
  const pointOptions = (pointsData?.items ?? []).map((point) => ({
    label: `${point.storeName} · ${point.address}`,
    value: point.id,
  }));

  const marketplaceOptions = marketplaces.map((m) => ({
    label: m.name,
    value: m.id,
  }));
  const storeOptions = stores.map((s) => ({ label: s.name, value: s.id }));
  const driverOptions = drivers.map((d) => ({
    label: `${d.name} ${d.lastName}`,
    value: d.id,
  }));

  const filters = {
    marketplaceId: marketplaceId ?? undefined,
    storeId: storeId ?? undefined,
    driverId: driverId ?? undefined,
    q,
    collectionPointId: pointId ?? undefined,
    status: statuses.length ? statuses : modeStatuses,
    type: types.length ? types : undefined,
    dateFrom: date ? date.startOf('day').toISOString() : undefined,
    dateTo: date ? date.add(1, 'day').startOf('day').toISOString() : undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isFetching, error, refetch } = useCollections(filters);
  const { mutateAsync: cancelCollection, isPending: isCancelling } =
    useCancelCollection();

  // Una Solicitada es de un cliente: el motivo es obligatorio porque lo ve él.
  const reasonRequired = toCancel?.status === 'requested';

  const closeCancel = () => {
    setToCancel(null);
    setCancelReason('');
  };

  const cancel = async () => {
    if (!toCancel) return;
    try {
      await cancelCollection({
        id: toCancel.id,
        reason: cancelReason.trim() || undefined,
      });
      message.success('Colecta cancelada');
      closeCancel();
    } catch (err: any) {
      message.error(err?.message ?? 'No se pudo cancelar la colecta');
    }
  };

  const columns: TableColumnsType<Collection> = [
    { title: '#', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Tienda',
      key: 'store',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue" style={{ margin: 0 }}>
            {record.storeName}
          </Tag>
          <span style={{ fontSize: 12 }}>{record.marketplaceName}</span>
        </Space>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (type: CollectionType | undefined, record) => {
        const value = collectionType(type);
        const declared = declaredText(
          record.declaredQuantity,
          record.declaredDescription,
        );
        return (
          <Space direction="vertical" size={0}>
            <Tag color={COLLECTION_TYPE_COLORS[value]} style={{ margin: 0 }}>
              {COLLECTION_TYPE_LABELS[value]}
            </Tag>
            {declared && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {declared}
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Inicio',
      dataIndex: 'scheduledStart',
      key: 'scheduledStart',
      width: 170,
      render: (value: string, record) =>
        record.type === 'on_demand'
          ? collectionWindow(
              value,
              record.scheduledEnd,
              'DD/MM/YYYY',
              record.utcOffset,
            )
          : dayjs(value).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Duración',
      dataIndex: 'estimatedDurationMinutes',
      key: 'estimatedDurationMinutes',
      width: 100,
      render: (value: number) => (value ? `${value} min` : '-'),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: CollectionStatus, record) => {
        const tag = (
          <Tag color={COLLECTION_STATUS_COLORS[status]}>
            {COLLECTION_STATUS_LABELS[status]}
          </Tag>
        );
        if (status === 'not_collected' && record.lastFailureReason) {
          return <Tooltip title={record.lastFailureReason}>{tag}</Tooltip>;
        }
        return tag;
      },
    },
    {
      title: 'Ruta',
      dataIndex: 'journeyId',
      key: 'journeyId',
      width: 120,
      render: (journeyId: string | null) =>
        journeyId ? (
          <Link to={`${SITELINKS.journey}/${journeyId}`}>{journeyId}</Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'right',
      width: 340,
      render: (_value, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailId(record.id)}
          >
            Ver detalle
          </Button>
          <Tooltip
            title={
              INGRESABLE.includes(record.status)
                ? 'Ingresar la colecta al depósito'
                : record.status === 'received'
                  ? 'Ya está ingresada'
                  : 'Se puede ingresar cuando el driver la retire'
            }
          >
            <Button
              size="small"
              type="primary"
              icon={<InboxOutlined />}
              disabled={!INGRESABLE.includes(record.status)}
              onClick={() => setIntakeId(record.id)}
            >
              Ingresar
            </Button>
          </Tooltip>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={isCancelling && toCancel?.id === record.id}
            disabled={!CANCELABLE.includes(record.status)}
            onClick={() => setToCancel(record)}
          >
            Cancelar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card title={title}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        De consulta y cancelación: las fijas se crean desde la ruta de colectas,
        al agregarle un punto, y las a demanda las piden los clientes. Las del
        modelo anterior siguen en{' '}
        <Link to={SITELINKS.collections.requests}>Solicitudes</Link>.
      </Typography.Paragraph>
      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching}
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Select
            style={{ width: '100%' }}
            options={marketplaceOptions}
            value={marketplaceId}
            onChange={(value) => {
              setPage(0);
              setMarketplaceId(value);
              setStoreId(null);
            }}
            placeholder="Filtrar por marketplace"
            loading={marketplacesLoading}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Select
            style={{ width: '100%' }}
            options={storeOptions}
            value={storeId}
            onChange={(value) => {
              setPage(0);
              setStoreId(value);
            }}
            placeholder="Filtrar por tienda"
            loading={storesLoading}
            disabled={!marketplaceId}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Select
            style={{ width: '100%' }}
            options={driverOptions}
            value={driverId}
            onChange={(value) => {
              setPage(0);
              setDriverId(value);
            }}
            placeholder="Filtrar por driver"
            loading={driversLoading}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Select
            style={{ width: '100%' }}
            options={pointOptions}
            value={pointId}
            onChange={(value) => {
              setPage(0);
              setPointId(value);
            }}
            placeholder="Filtrar por punto de colecta"
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <RouteSearch
            placeholder="Buscar por id, ruta, tienda o driver"
            onSearch={onSearch}
          />
        </Col>
        <Col>
          <DatePicker
            placeholder="Fecha de la colecta"
            format="DD/MM/YYYY"
            value={date}
            onChange={(value) => {
              setPage(0);
              setDate(value);
            }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 180 }}
            placeholder="Tipo"
            value={types}
            options={TYPE_OPTIONS}
            onChange={(value) => {
              setPage(0);
              setTypes(value);
            }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 220 }}
            placeholder="Estado"
            value={statuses}
            options={statusOptions}
            onChange={(value) => {
              setPage(0);
              setStatuses(value);
            }}
          />
        </Col>
      </Row>

      {error ? (
        <Result
          status={collectionsResultStatus(error)}
          title="Algo salió mal..."
          subTitle={(error as Error).message}
          extra={[
            <Button type="primary" key="retry" onClick={() => refetch()}>
              Reintentar
            </Button>,
          ]}
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isFetching}
          locale={{
            emptyText:
              mode === 'transit'
                ? 'No hay colectas en tránsito'
                : 'No se encontraron colectas',
          }}
          pagination={{
            current: page + 1,
            pageSize: PAGE_SIZE,
            total: data?.totalElements ?? 0,
            showSizeChanger: false,
            onChange: (nextPage) => setPage(nextPage - 1),
          }}
        />
      )}

      <CollectionDetailModal
        collectionId={detailId}
        onClose={() => setDetailId(null)}
      />

      <Modal
        open={toCancel != null}
        title="Cancelar la colecta"
        okText="Cancelar colecta"
        cancelText="Volver"
        okButtonProps={{
          danger: true,
          disabled: reasonRequired && !cancelReason.trim(),
        }}
        confirmLoading={isCancelling}
        onOk={cancel}
        onCancel={closeCancel}
        destroyOnClose
      >
        <Typography.Paragraph>
          {toCancel?.journeyId
            ? `Está en la ruta ${toCancel.journeyId}. Se saca de la ruta y no se va a colectar.`
            : 'No se va a colectar.'}
        </Typography.Paragraph>
        <Input.TextArea
          autoFocus
          rows={3}
          maxLength={200}
          value={cancelReason}
          placeholder={
            reasonRequired ? 'Motivo (lo ve el cliente)' : 'Motivo (opcional)'
          }
          onChange={(event) => setCancelReason(event.target.value)}
        />
      </Modal>

      <Modal
        open={intakeId != null}
        onCancel={() => setIntakeId(null)}
        width={1000}
        footer={null}
        destroyOnClose
        title="Ingresar colecta al depósito"
      >
        {intakeId != null && (
          <CollectionV2Intake
            collectionId={intakeId}
            onReset={() => setIntakeId(null)}
            showBack={false}
          />
        )}
      </Modal>
    </Card>
  );
};

const CollectionsTransit: React.FC = () => <Collections mode="transit" />;
export const CollectionsHistory: React.FC = () => (
  <Collections mode="history" />
);

export default CollectionsTransit;
